import React, { useState } from 'react';
import { trackNewsletterSignup } from '../../../../util/gtmUtils';
import {
  arrayOf,
  bool,
  func,
  node,
  number,
  object,
  shape,
  string,
} from 'prop-types';
import classNames from 'classnames';
import { ReactComponent as Logo } from '../../../../assets/UI-Logo-Horizontal.svg';
// import { LinkedLogo } from '../../../../components';

import Field from '../../Field';
import BlockBuilder from '../../BlockBuilder';

import SectionContainer from '../SectionContainer';
import css from './SectionFooter.module.css';
import { apiBaseUrl } from '../../../../util/api';

// The number of columns (numberOfColumns) affects styling

const GRID_CONFIG = [
  { contentCss: css.contentCol1, gridCss: css.gridCol1 },
  { contentCss: css.contentCol2, gridCss: css.gridCol2 },
  { contentCss: css.contentCol3, gridCss: css.gridCol3 },
  { contentCss: css.contentCol4, gridCss: css.gridCol4 },
];
const MAX_MOBILE_SCREEN_WIDTH = 1024;

const getIndex = numberOfColumns => numberOfColumns - 1;

const getContentCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.contentCss : GRID_CONFIG[0].contentCss;
};

const getGridCss = numberOfColumns => {
  const contentConfig = GRID_CONFIG[getIndex(numberOfColumns)];
  return contentConfig ? contentConfig.gridCss : GRID_CONFIG[0].gridCss;
};

const SendGridForm = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle, sending, success, error
  const [buttonText, setButtonText] = useState('Subscribe');
  const [disabled, setDisabled] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async e => {
    e.preventDefault();

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      setErrorMessage('Please enter a valid email address');
      return;
    }

    try {
      setStatus('sending');
      setButtonText('Sending...');
      setDisabled(true);
      setErrorMessage('');

      // Call the backend API endpoint directly
      const response = await fetch(`${apiBaseUrl()}/api/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Subscription failed');
      }

      setStatus('success');
      setButtonText('Subscribed!');
      trackNewsletterSignup();
    } catch (error) {
      console.error('Newsletter signup error:', error);
      setStatus('error');
      setButtonText('Error');
      setErrorMessage(
        error.message || 'Subscription failed. Please try again.'
      );

      setTimeout(() => {
        setStatus('idle');
        setButtonText('Subscribe');
        setDisabled(false);
      }, 3000);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className={css.sendgridForm}>
        <input
          type="email"
          placeholder="Your Email"
          value={email}
          onChange={e => setEmail(e.target.value)}
          disabled={disabled}
        />
        <button disabled={disabled} type="submit">
          {buttonText}
        </button>
        {errorMessage && (
          <span className={css.msg - alert}>{errorMessage}</span>
        )}
      </div>
    </form>
  );
};

// Section component that's able to show blocks in multiple different columns (defined by "numberOfColumns" prop)
const SectionFooter = props => {
  const {
    sectionId,
    className,
    rootClassName,
    numberOfColumns,
    socialMediaLinks,
    slogan,
    appearance,
    copyright,
    blocks,
    options,
    linkLogoToExternalSite,
  } = props;

  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const fieldComponents = options?.fieldComponents;
  const fieldOptions = { fieldComponents };
  const linksWithBlockId = socialMediaLinks?.map(sml => {
    return {
      ...sml,
      blockId: sml.link.platform,
    };
  });

  const showSocialMediaLinks = socialMediaLinks?.length > 0;
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;
  const logoLayout = isMobileLayout ? 'mobile' : 'desktop';

  // use block builder instead of mapping blocks manually
  return (
    <SectionContainer
      as="footer"
      id={sectionId}
      className={className || css.root}
      rootClassName={rootClassName}
      appearance={appearance}
      options={fieldOptions}
      style={{
        backgroundColor: '#0000FF',
        color: 'white',
        padding: '0',
        margin: '0',
      }}
    >
      <div className={css.footer}>
        <div
          className={classNames(css.content, getContentCss(numberOfColumns))}
        >
          {/* <div>
            <LinkedLogo
              rootClassName={css.logoLink}
              logoClassName={css.logoWrapper}
              logoImageClassName={css.logoImage}
              linkToExternalSite={linkLogoToExternalSite}
              layout={logoLayout}
            />
          </div> */}
          <div className={css.sloganMobile}>
            <span style={{ fontSize: '2rem', lineHeight: '2.5rem' }}>
              Be the one that tells your friends. <br />
              Sign up and be the first to know what's what.
            </span>
            {typeof window !== 'undefined' && <SendGridForm />}
          </div>
          <div className={css.detailsInfo}>
            <div className={css.sloganDesktop}>
              {/* <Field data={slogan} className={css.slogan} /> */}
              <span style={{ fontSize: '2rem', lineHeight: '2.5rem' }}>
                Be the one that tells your friends.
                <br />
                Sign up and be the first to know what's what.
              </span>
              {typeof window !== 'undefined' && <SendGridForm />}
            </div>
            {/* {showSocialMediaLinks ? (
              <div className={css.icons}>
                <BlockBuilder blocks={linksWithBlockId} sectionId={sectionId} options={options} />
              </div>
            ) : null} */}
            <Field data={copyright} className={css.copyright} />
          </div>
          <div className={classNames(css.grid, getGridCss(numberOfColumns))}>
            <BlockBuilder
              blocks={blocks}
              sectionId={sectionId}
              options={options}
            />
          </div>
        </div>
        <Logo style={{ color: 'white' }} />
      </div>
    </SectionContainer>
  );
};

const propTypeOption = shape({
  fieldComponents: shape({ component: node, pickValidProps: func }),
});

SectionFooter.defaultProps = {
  className: null,
  rootClassName: null,
  textClassName: null,
  numberOfColumns: 1,
  socialMediaLinks: [],
  slogan: null,
  copyright: null,
  appearance: null,
  blocks: [],
  options: null,
};

SectionFooter.propTypes = {
  sectionId: string.isRequired,
  className: string,
  rootClassName: string,
  numberOfColumns: number,
  socialMediaLinks: arrayOf(object),
  slogan: object,
  copyright: object,
  appearance: object,
  blocks: arrayOf(object),
  options: propTypeOption,
};

export default SectionFooter;
